import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { boardPackApi, BoardPackResult } from '@/lib/boardPack';
import { api } from '@/lib/api';
import { formatDateForLng } from '@/lib/wareki';
import { AppShell } from '@/components/app/AppShell';
import { MarkdownText } from '@/components/MarkdownText';
import { useToast, errMessage } from '@/components/Toast';

interface WorkspaceLite { id: string; name: string; fyLabel: string }

export function BoardPackPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { wsId } = useParams<{ wsId: string }>();
  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<WorkspaceLite>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });

  const now = new Date();
  const lastOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
  const firstOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);

  const [audience, setAudience] = useState<'board' | 'cfo' | 'investor'>('board');
  const [periodStart, setPeriodStart] = useState(firstOfPrevMonth);
  const [periodEnd, setPeriodEnd] = useState(lastOfPrevMonth);

  const write = useMutation({
    mutationFn: () => boardPackApi.write(wsId!, { audience, periodStart, periodEnd }),
    onError: (e) => toast.error(errMessage(e, t('board_pack.error', { defaultValue: 'Could not generate the board pack. Please try again.' }))),
  });
return (
    <AppShell title={t('board_pack.title')} subtitle={t('board_pack.subtitle')}>
<main className="mx-auto max-w-4xl px-6 py-10 space-y-6 print:max-w-none print:px-0 print:py-0">
        <div className="print:hidden">
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('board_pack.title')}</h1>
          <p className="mt-1 text-ink-600">{t('board_pack.subtitle')}</p>
        </div>

        <div className="rounded-md border border-border bg-white p-5 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">{t('board_pack.audience')}</span>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value as typeof audience)}
                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30 outline-none"
              >
                <option value="board">{t('board_pack.audience_board')}</option>
                <option value="cfo">{t('board_pack.audience_cfo')}</option>
                <option value="investor">{t('board_pack.audience_investor')}</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">{t('board_pack.period_start')}</span>
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="num w-full rounded-lg border border-border bg-white px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="block text-xs text-muted-foreground mb-1">{t('board_pack.period_end')}</span>
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="num w-full rounded-lg border border-border bg-white px-3 py-2 text-sm" />
            </label>
          </div>
          <button
            onClick={() => write.mutate()}
            disabled={write.isPending}
            className="mt-4 inline-flex items-center px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 disabled:opacity-60 transition"
          >
            {write.isPending ? t('board_pack.writing') : t('board_pack.draft')}
          </button>
          {write.error && (
            <div className="mt-3 rounded-lg bg-warn-500/10 border border-warn-500/30 text-warn-600 px-3 py-2 text-sm">
              {(write.error as Error).message}
            </div>
          )}
        </div>

        {write.data && (
          <BoardPackView
            pack={write.data}
            wsId={wsId!}
            t={t}
            onDownload={async () => {
              const blob = await boardPackApi.downloadPdf(wsId!, { audience, periodStart, periodEnd });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `cadencez-board-pack-${audience}-${periodStart}_${periodEnd}.pdf`;
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 5000);
            }}
          />
        )}
      </main>
    </AppShell>
  );
}

function BoardPackView({ pack, wsId, t, onDownload }: {
  pack: BoardPackResult;
  wsId: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onDownload: () => void | Promise<void>;
}) {
  const { i18n } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const periodStart = formatDateForLng(pack.period.start, i18n.language);
  const periodEnd = formatDateForLng(pack.period.end, i18n.language);
  const approvalTargetId = `${pack.audience} ${pack.period.start}→${pack.period.end}`;
  return (
    <article className="rounded-md border border-border bg-white p-8 space-y-6 print:border-0 print:rounded-none print:shadow-none print:p-0">
      <header className="flex items-baseline justify-between border-b border-ink-100 pb-4">
        <div>
          <h2 className="display text-2xl font-semibold text-foreground">{t('board_pack.draft_title')}</h2>
          <p className="text-xs num text-muted-foreground mt-1">
            {pack.audience} · {periodStart} → {periodEnd} · {t('board_pack.ai_byline')}
            {pack.citedFactIds?.length > 0 && (
              <> · {t('variance.cites_n', { count: pack.citedFactIds.length })}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            onClick={async () => {
              setDownloading(true);
              try { await onDownload(); } finally { setDownloading(false); }
            }}
            disabled={downloading}
            className="px-3 py-1.5 rounded-md border border-border text-ink-700 text-xs hover:bg-foreground/[0.04] disabled:opacity-60"
          >
            {downloading ? t('common.loading') : t('board_pack.download_pdf')}
          </button>
          <Link
            to={`/app/workspace/${wsId}/approvals?targetKind=board_pack&targetId=${encodeURIComponent(approvalTargetId)}`}
            className="px-3 py-1.5 rounded-md border border-border text-ink-700 text-xs hover:bg-foreground/[0.04]"
          >
            {t('approvals.send_for_approval', { defaultValue: 'Send for approval' })}
          </Link>
          <button
            onClick={() => window.print()}
            className="px-3 py-1.5 rounded-md border border-border text-ink-700 text-xs hover:bg-foreground/[0.04]"
          >
            {t('board_pack.print')}
          </button>
        </div>
      </header>
      {pack.sections.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('board_pack.empty')}</p>
      ) : (
        pack.sections.map((s, i) => (
          <section key={i}>
            <h3 className="display text-xl font-semibold text-foreground mb-2">{s.heading}</h3>
            <MarkdownText className="text-base">{s.body}</MarkdownText>
          </section>
        ))
      )}

      {pack.figures && pack.figures.topAccounts.length > 0 && (
        <section>
          <h3 className="display text-xl font-semibold text-foreground mb-2">
            {t('board_pack.key_figures')} <span className="num text-sm text-muted-foreground font-normal">({pack.figures.currency})</span>
          </h3>
          {(() => {
            const maxAbs = Math.max(1, ...pack.figures.topAccounts.map((f) => Math.abs(f.total)));
            return (
              <div className="mb-4 space-y-1.5">
                {pack.figures.topAccounts.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="num w-40 shrink-0 truncate text-ink-700">{f.accountCode}</span>
                    <div className="flex-1 bg-ink-50 rounded-sm h-3.5 overflow-hidden">
                      <div
                        className={'h-full ' + (f.total < 0 ? 'bg-amber-500' : 'bg-emerald-500')}
                        style={{ width: `${Math.max(1, (Math.abs(f.total) / maxAbs) * 100)}%` }}
                      />
                    </div>
                    <span className="num w-28 shrink-0 text-right tabular-nums text-ink-600">{f.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            );
          })()}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-ink-100">
                <th className="py-1.5 pr-3 font-medium text-[11px] uppercase tracking-[0.14em]">{t('board_pack.fig_account')}</th>
                <th className="py-1.5 px-3 font-medium text-[11px] uppercase tracking-[0.14em]">{t('board_pack.fig_scenario')}</th>
                <th className="py-1.5 pl-3 font-medium text-[11px] uppercase tracking-[0.14em] text-right">{t('board_pack.fig_total')}</th>
              </tr>
            </thead>
            <tbody>
              {pack.figures.topAccounts.map((f, i) => (
                <tr key={i} className="border-b border-ink-50">
                  <td className="py-1.5 pr-3 num font-medium">
                    <Link to={`/app/workspace/${wsId}/account-search?q=${encodeURIComponent(f.accountCode)}`} className="text-foreground hover:text-emerald-700 hover:underline print:no-underline print:text-foreground">
                      {f.accountCode}
                    </Link>
                  </td>
                  <td className="py-1.5 px-3 num text-ink-600">{f.scenario}</td>
                  <td className="py-1.5 pl-3 num text-right tabular-nums text-foreground">{f.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {pack.signers && pack.signers.length > 0 && (
        <section className="border-t border-ink-100 pt-6">
          <h3 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-3">{t('board_pack.signoff')}</h3>
          <div className="flex flex-wrap gap-5">
            {pack.signers.map((s, i) => (
              <div key={i} className="flex flex-col items-center w-24 text-center">
                <div
                  className={
                    'h-16 w-16 rounded-full flex items-center justify-center text-[11px] font-semibold ' +
                    (s.approvedAt
                      ? 'border-2 border-rose-500 text-rose-600'
                      : 'border-2 border-dashed border-ink-300 text-ink-400')
                  }
                >
                  <span className="truncate px-1">{s.name}</span>
                </div>
                <span className="mt-1.5 text-xs text-ink-700 truncate w-full">{s.role}</span>
                <span className="num text-[10px] text-muted-foreground">{s.approvedAt ?? t('board_pack.pending')}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
